import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Maximize2, ChevronRight, ExternalLink } from 'lucide-react';
import { Button } from './ui/button';

// Modal for expanded view
export function ExpandedModal({ isOpen, onClose, title, children, icon: Icon, color = 'blue' }) {
  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-[#0a1729]/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          transition={{ duration: 0.2 }}
          className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-4xl max-h-[75vh] overflow-hidden shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-slate-800">
            <div className="flex items-center gap-3">
              {Icon && (
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center bg-${color}-500/20`}>
                  <Icon className={`w-5 h-5 text-${color}-400`} />
                </div>
              )}
              <h2 className="font-outfit text-xl font-semibold text-white">{title}</h2>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="text-slate-300 hover:text-white hover:bg-slate-800"
            >
              <X className="w-5 h-5" />
            </Button>
          </div>

          {/* Content */}
          <div className="p-6 overflow-y-auto max-h-[calc(75vh-80px)]">
            {children}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

// Clickable card item that expands on click
export function ExpandableItem({ 
  title, 
  subtitle, 
  description, 
  badge, 
  badgeColor = 'blue',
  icon: Icon,
  iconColor = 'blue',
  metadata = [],
  expandedContent,
  onClick,
  className = ''
}) {
  const [isExpanded, setIsExpanded] = useState(false);

  const handleClick = () => {
    if (expandedContent) {
      setIsExpanded(true);
    }
    if (onClick) onClick();
  };

  return (
    <>
      <motion.div
        whileHover={{ scale: 1.01, backgroundColor: 'rgba(51, 65, 85, 0.5)' }}
        whileTap={{ scale: 0.99 }}
        onClick={handleClick}
        className={`p-4 bg-slate-800/30 rounded-xl border border-slate-700/50 cursor-pointer 
          hover:border-${iconColor}-500/30 transition-all group ${className}`}
        data-testid="expandable-item"
      >
        <div className="flex gap-4">
          {Icon && (
            <div className={`w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 bg-${iconColor}-500/20`}>
              <Icon className={`w-6 h-6 text-${iconColor}-400`} />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="font-outfit font-semibold text-white group-hover:text-blue-400 transition-colors">
                {title}
              </h3>
              {badge && (
                <span className={`px-2 py-0.5 rounded text-xs bg-${badgeColor}-500/20 text-${badgeColor}-400`}>
                  {badge}
                </span>
              )}
              <Maximize2 className="w-4 h-4 text-slate-300 opacity-0 group-hover:opacity-100 transition-opacity ml-auto" />
            </div>
            {subtitle && <p className="text-slate-300 text-sm mb-1">{subtitle}</p>}
            <p className="text-slate-300 text-sm line-clamp-2">{description}</p>
            {metadata.length > 0 && (
              <div className="flex flex-wrap gap-3 mt-2 text-xs text-slate-300">
                {metadata.map((item, i) => (
                  <span key={i} className="flex items-center gap-1">
                    {item.icon && <item.icon className="w-3 h-3" />}
                    {item.text}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      </motion.div>

      {/* Expanded Modal */}
      <ExpandedModal
        isOpen={isExpanded}
        onClose={() => setIsExpanded(false)}
        title={title}
        icon={Icon}
        color={iconColor}
      >
        {expandedContent || (
          <div className="space-y-6">
            {subtitle && (
              <p className="text-lg text-slate-300">{subtitle}</p>
            )}
            <div className="prose prose-invert max-w-none">
              <p className="text-slate-300 text-base leading-relaxed">{description}</p>
            </div>
            {metadata.length > 0 && (
              <div className="flex flex-wrap gap-4 pt-4 border-t border-slate-800">
                {metadata.map((item, i) => (
                  <div key={i} className="flex items-center gap-2 text-slate-300">
                    {item.icon && <item.icon className="w-4 h-4" />}
                    <span>{item.text}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </ExpandedModal>
    </>
  );
}

// Pathway card with full expanded view
export function PathwayCard({ path, index }) {
  const [isExpanded, setIsExpanded] = useState(false);
  
  const typeIcons = {
    college: '🎓',
    trade: '🔧',
    hybrid: '⚡',
    military: '🎖️',
    entrepreneurship: '🚀'
  };

  const typeColors = {
    college: 'blue',
    trade: 'emerald',
    hybrid: 'purple',
    military: 'amber',
    entrepreneurship: 'cyan'
  };

  const color = typeColors[path.type] || 'blue';
  const icon = typeIcons[path.type] || '📍';

  return (
    <>
      <motion.div
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        onClick={() => setIsExpanded(true)}
        className={`p-5 bg-slate-800/40 rounded-xl border border-slate-700/50 cursor-pointer 
          hover:border-${color}-500/40 hover:bg-slate-800/60 transition-all group`}
        data-testid={`pathway-card-${index}`}
      >
        <div className="flex items-start gap-4">
          <div className={`w-14 h-14 rounded-xl flex items-center justify-center text-2xl bg-${color}-500/20`}>
            {icon}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-2">
              <h3 className="font-outfit text-lg font-semibold text-white group-hover:text-blue-400 transition-colors">
                {path.title}
              </h3>
              <span className={`px-2.5 py-1 rounded-full text-xs font-medium bg-${color}-500/20 text-${color}-400`}>
                {path.match_score}% match
              </span>
            </div>
            <p className="text-slate-300 text-sm line-clamp-2 mb-3">{path.description}</p>
            <div className="flex items-center gap-4 text-sm">
              <span className="text-slate-300">⏱️ {path.timeline}</span>
              <span className="text-slate-300">💰 {path.estimated_cost}</span>
            </div>
          </div>
          <ChevronRight className="w-5 h-5 text-slate-300 group-hover:text-blue-400 group-hover:translate-x-1 transition-all" />
        </div>
      </motion.div>

      {/* Expanded Modal for Pathway */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-[#0a1729]/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setIsExpanded(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 30 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 30 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="bg-gradient-to-b from-slate-900 to-slate-950 border border-slate-700 rounded-2xl w-full max-w-3xl max-h-[75vh] overflow-hidden shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className={`p-8 bg-gradient-to-r from-${color}-600/20 to-transparent border-b border-slate-800`}>
                <div className="flex items-center justify-between mb-4">
                  <div className={`w-16 h-16 rounded-xl flex items-center justify-center text-3xl bg-${color}-500/30`}>
                    {icon}
                  </div>
                  <Button
                    variant="ghost"
                    onClick={() => setIsExpanded(false)}
                    className="text-slate-300 hover:text-white"
                  >
                    <X className="w-6 h-6" />
                  </Button>
                </div>
                <h2 className="font-outfit text-2xl font-bold text-white mb-2">{path.title}</h2>
                <div className="flex items-center gap-4">
                  <span className={`px-3 py-1.5 rounded-full text-sm font-medium bg-${color}-500/30 text-${color}-300`}>
                    {path.match_score}% Match Score
                  </span>
                  <span className="text-slate-300 text-sm capitalize">{path.type} pathway</span>
                </div>
              </div>

              {/* Content */}
              <div className="p-8 overflow-y-auto max-h-[calc(75vh-180px)] space-y-6">
                {/* Description */}
                <div>
                  <h3 className="text-lg font-semibold text-white mb-3">About This Path</h3>
                  <p className="text-slate-300 text-base leading-relaxed">{path.description}</p>
                </div>

                {/* Key Details */}
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="p-4 bg-slate-800/50 rounded-xl">
                    <div className="text-slate-300 text-sm mb-1">Timeline</div>
                    <div className="text-white font-medium text-lg">{path.timeline}</div>
                  </div>
                  <div className="p-4 bg-slate-800/50 rounded-xl">
                    <div className="text-slate-300 text-sm mb-1">Estimated Cost</div>
                    <div className="text-emerald-400 font-medium text-lg">{path.estimated_cost}</div>
                  </div>
                </div>

                {/* Next Steps */}
                {path.next_steps && path.next_steps.length > 0 && (
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-3">Recommended Next Steps</h3>
                    <div className="space-y-2">
                      {path.next_steps.map((step, i) => (
                        <div key={i} className="flex items-start gap-3 p-3 bg-slate-800/30 rounded-lg">
                          <span className="w-6 h-6 rounded-full bg-blue-500/20 text-blue-400 text-sm flex items-center justify-center flex-shrink-0">
                            {i + 1}
                          </span>
                          <span className="text-slate-300">{step}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Key Skills */}
                {path.key_skills && path.key_skills.length > 0 && (
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-3">Key Skills to Develop</h3>
                    <div className="flex flex-wrap gap-2">
                      {path.key_skills.map((skill, i) => (
                        <span key={i} className="px-3 py-1.5 bg-slate-800/60 border border-slate-700 rounded-full text-sm text-slate-300">
                          {skill}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Potential Careers */}
                {path.potential_careers && path.potential_careers.length > 0 && (
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-3">Career Opportunities</h3>
                    <div className="grid sm:grid-cols-2 gap-3">
                      {path.potential_careers.map((career, i) => (
                        <div key={i} className="p-3 bg-slate-800/40 rounded-lg border border-slate-700/50 text-slate-300">
                          {career}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Footer Actions */}
              <div className="p-6 border-t border-slate-800 flex gap-3">
                <Button className="btn-primary flex-1">
                  Add to My Goals
                </Button>
                <Button variant="outline" className="border-slate-700 text-slate-300">
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Explore More
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

// Action Plan item with expanded details
export function ActionPlanCard({ action, index }) {
  const [isExpanded, setIsExpanded] = useState(false);

  const priorityColors = {
    high: 'amber',
    medium: 'blue',
    low: 'slate'
  };

  const color = priorityColors[action.priority] || 'blue';

  return (
    <>
      <motion.div
        whileHover={{ scale: 1.01, backgroundColor: 'rgba(51, 65, 85, 0.5)' }}
        whileTap={{ scale: 0.99 }}
        onClick={() => setIsExpanded(true)}
        className="flex items-start gap-3 p-4 bg-slate-800/30 rounded-xl border border-slate-700/50 cursor-pointer hover:border-blue-500/30 transition-all group"
        data-testid={`action-card-${index}`}
      >
        <div className="w-10 h-10 rounded-full bg-blue-600/20 flex items-center justify-center flex-shrink-0">
          <span className="text-blue-400 text-sm font-mono font-bold">D{action.day}</span>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <p className="text-white font-medium group-hover:text-blue-400 transition-colors">{action.title}</p>
            <span className={`px-2 py-0.5 rounded text-xs bg-${color}-500/20 text-${color}-400`}>
              {action.priority}
            </span>
          </div>
          <p className="text-slate-300 text-sm line-clamp-1">{action.description}</p>
          <span className="text-xs text-slate-300 mt-1 block">{action.time_estimate}</span>
        </div>
        <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-blue-400 group-hover:translate-x-1 transition-all mt-3" />
      </motion.div>

      {/* Expanded Modal */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-[#0a1729]/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setIsExpanded(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 30 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 30 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-2xl max-h-[75vh] overflow-hidden shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="p-5 border-b border-slate-800">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-blue-600/20 flex items-center justify-center">
                      <span className="text-blue-400 text-lg font-mono font-bold">D{action.day}</span>
                    </div>
                    <div>
                      <h2 className="font-outfit text-lg font-semibold text-white">{action.title}</h2>
                      <div className="flex items-center gap-3 mt-1">
                        <span className={`px-2.5 py-1 rounded-full text-xs font-medium bg-${color}-500/20 text-${color}-400`}>
                          {action.priority} priority
                        </span>
                        <span className="text-slate-300 text-sm">⏱️ {action.time_estimate}</span>
                      </div>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    onClick={() => setIsExpanded(false)}
                    className="text-slate-300 hover:text-white"
                  >
                    <X className="w-5 h-5" />
                  </Button>
                </div>
              </div>

              {/* Content */}
              <div className="p-5 space-y-4 overflow-y-auto max-h-[calc(75vh-160px)]">
                <div>
                  <h3 className="text-sm font-medium text-slate-300 mb-2">Description</h3>
                  <p className="text-slate-200 text-base leading-relaxed">{action.description}</p>
                </div>

                {action.tips && action.tips.length > 0 && (
                  <div>
                    <h3 className="text-sm font-medium text-slate-300 mb-2">Tips</h3>
                    <ul className="space-y-2">
                      {action.tips.map((tip, i) => (
                        <li key={i} className="flex items-start gap-2 text-slate-300">
                          <span className="text-emerald-400">✓</span>
                          {tip}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {action.resources && action.resources.length > 0 && (
                  <div>
                    <h3 className="text-sm font-medium text-slate-300 mb-2">Helpful Resources</h3>
                    <div className="space-y-2">
                      {action.resources.map((resource, i) => (
                        <a 
                          key={i}
                          href={resource.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 p-3 bg-slate-800/50 rounded-lg hover:bg-slate-800 transition-colors text-blue-400"
                        >
                          <ExternalLink className="w-4 h-4" />
                          {resource.name || resource.url}
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="p-6 border-t border-slate-800 flex gap-3">
                <Button className="btn-primary flex-1">
                  Mark as Complete
                </Button>
                <Button variant="outline" className="border-slate-700 text-slate-300">
                  Add to Goals
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

// Scholarship card with expanded details
export function ScholarshipCard({ scholarship, index }) {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <>
      <motion.div
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        onClick={() => setIsExpanded(true)}
        className="p-4 bg-slate-800/30 rounded-xl border border-slate-700/50 cursor-pointer hover:border-emerald-500/30 hover:bg-slate-800/50 transition-all group"
        data-testid={`scholarship-card-${index}`}
      >
        <div className="flex items-center justify-between mb-2">
          <p className="text-white font-medium group-hover:text-emerald-400 transition-colors line-clamp-1 flex-1">
            {scholarship.name}
          </p>
          <span className="text-emerald-400 font-mono text-lg font-semibold ml-2">{scholarship.amount}</span>
        </div>
        <div className="flex items-center gap-3 text-sm">
          <span className="text-slate-300">📅 {scholarship.deadline}</span>
          <span className="text-cyan-400 font-medium">{scholarship.match_score}% match</span>
        </div>
        <div className="flex items-center justify-between mt-2">
          <span className="text-slate-300 text-xs">Click to view details</span>
          <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-emerald-400 group-hover:translate-x-1 transition-all" />
        </div>
      </motion.div>

      {/* Expanded Modal */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-[#0a1729]/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setIsExpanded(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 30 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 30 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-2xl max-h-[75vh] overflow-hidden shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="p-5 bg-gradient-to-r from-emerald-600/20 to-transparent border-b border-slate-800">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="font-outfit text-lg font-semibold text-white mb-2">{scholarship.name}</h2>
                    <div className="flex items-center gap-4">
                      <span className="text-2xl font-bold text-emerald-400">{scholarship.amount}</span>
                      <span className="px-3 py-1 rounded-full text-sm bg-cyan-500/20 text-cyan-400">
                        {scholarship.match_score}% match
                      </span>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    onClick={() => setIsExpanded(false)}
                    className="text-slate-300 hover:text-white"
                  >
                    <X className="w-5 h-5" />
                  </Button>
                </div>
              </div>

              {/* Content */}
              <div className="p-5 space-y-4 overflow-y-auto max-h-[calc(75vh-160px)]">
                <div className="grid sm:grid-cols-2 gap-3">
                  <div className="p-3 bg-slate-800/50 rounded-xl">
                    <div className="text-slate-300 text-sm mb-1">Deadline</div>
                    <div className="text-white font-medium">📅 {scholarship.deadline}</div>
                  </div>
                  <div className="p-3 bg-slate-800/50 rounded-xl">
                    <div className="text-slate-300 text-sm mb-1">Award Amount</div>
                    <div className="text-emerald-400 font-bold text-lg">{scholarship.amount}</div>
                  </div>
                </div>

                {scholarship.requirements && scholarship.requirements.length > 0 && (
                  <div>
                    <h3 className="text-sm font-medium text-slate-300 mb-3">Requirements</h3>
                    <div className="space-y-2">
                      {scholarship.requirements.map((req, i) => (
                        <div key={i} className="flex items-start gap-2 p-3 bg-slate-800/30 rounded-lg">
                          <span className="text-amber-400">•</span>
                          <span className="text-slate-300">{req}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {scholarship.description && (
                  <div>
                    <h3 className="text-sm font-medium text-slate-300 mb-2">About This Scholarship</h3>
                    <p className="text-slate-300 leading-relaxed">{scholarship.description}</p>
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="p-6 border-t border-slate-800 flex gap-3">
                {scholarship.apply_url ? (
                  <a
                    href={scholarship.apply_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-primary flex-1 flex items-center justify-center gap-2"
                  >
                    <ExternalLink className="w-4 h-4" />
                    Apply Now
                  </a>
                ) : (
                  <Button className="btn-primary flex-1">Apply Now</Button>
                )}
                <Button variant="outline" className="border-slate-700 text-slate-300">
                  Add to Goals
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

// College card with expanded details
export function CollegeCard({ college, onToggleFavorite, isFavorite, borderColor = 'slate' }) {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <>
      <motion.div
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        onClick={() => setIsExpanded(true)}
        className={`relative p-5 bg-slate-800/50 rounded-xl border border-${borderColor}-500/20 hover:border-${borderColor}-500/50 transition-all cursor-pointer group`}
        data-testid={`college-card-${college.name?.replace(/\s+/g, '-').toLowerCase()}`}
      >
        {/* Favorite button */}
        {onToggleFavorite && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onToggleFavorite(college);
            }}
            className={`absolute top-4 right-4 p-2 rounded-full transition-colors z-10 ${
              isFavorite
                ? 'bg-pink-500 text-white'
                : 'bg-slate-700 text-slate-300 hover:text-pink-400'
            }`}
          >
            <svg className="w-4 h-4" fill={isFavorite ? "currentColor" : "none"} stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
            </svg>
          </button>
        )}

        <h4 className="text-white font-semibold text-lg pr-10 group-hover:text-blue-400 transition-colors">
          {college.name}
        </h4>
        <p className="text-slate-300 text-sm flex items-center gap-1 mt-1">
          📍 {college.location}
        </p>
        
        <div className="mt-4 grid grid-cols-2 gap-3">
          {college.acceptance_rate ? (
            <div className="p-2 bg-slate-900/50 rounded-lg">
              <div className="text-slate-300 text-xs">Acceptance</div>
              <div className="text-amber-400 font-mono">{college.acceptance_rate}</div>
            </div>
          ) : (
            <div className="p-2 bg-slate-900/50 rounded-lg">
              <div className="text-slate-300 text-xs">Admissions</div>
              <div className="text-cyan-300 font-mono text-sm">Open enrollment</div>
            </div>
          )}
          {college.tuition && (
            <div className="p-2 bg-slate-900/50 rounded-lg">
              <div className="text-slate-300 text-xs">Tuition</div>
              <div className="text-emerald-400 font-mono text-sm">{college.tuition}</div>
            </div>
          )}
          {college.graduation_rate && (
            <div className="p-2 bg-slate-900/50 rounded-lg">
              <div className="text-slate-300 text-xs">Graduation</div>
              <div className="text-cyan-400 font-mono text-sm">{college.graduation_rate}</div>
            </div>
          )}
          {college.earnings_10yr && (
            <div className="p-2 bg-slate-900/50 rounded-lg">
              <div className="text-slate-300 text-xs">Earnings 10yr</div>
              <div className="text-violet-300 font-mono text-sm">{college.earnings_10yr}</div>
            </div>
          )}
        </div>
        
        {college.known_for && (
          <p className="text-xs text-slate-300 mt-3 line-clamp-1">{college.known_for}</p>
        )}
        
        <p className="text-slate-300 text-xs mt-3 text-center opacity-0 group-hover:opacity-100 transition-opacity">
          Click for details
        </p>
      </motion.div>

      {/* Expanded Modal */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-[#0a1729]/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setIsExpanded(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 30 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 30 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-3xl max-h-[75vh] overflow-hidden shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="p-6 bg-gradient-to-r from-blue-600/20 to-purple-600/10 border-b border-slate-800">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 bg-gradient-to-br from-blue-600/30 to-purple-600/30 rounded-xl flex items-center justify-center text-3xl">
                      🎓
                    </div>
                    <div>
                      <h2 className="font-outfit text-2xl font-bold text-white">{college.name}</h2>
                      <p className="text-slate-300 flex items-center gap-2 mt-1">
                        📍 {college.location}
                      </p>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    onClick={() => setIsExpanded(false)}
                    className="text-slate-300 hover:text-white"
                  >
                    <X className="w-6 h-6" />
                  </Button>
                </div>
              </div>

              {/* Content */}
              <div className="p-6 overflow-y-auto max-h-[calc(75vh-180px)] space-y-6">
                {/* Key Stats */}
                <div className="grid sm:grid-cols-3 gap-4">
                  <div className="p-5 bg-slate-800/50 rounded-xl text-center">
                    <div className="text-slate-300 text-sm mb-2">Acceptance Rate</div>
                    <div className="text-amber-400 font-mono text-2xl font-bold">{college.acceptance_rate || 'Open enrollment'}</div>
                  </div>
                  <div className="p-5 bg-slate-800/50 rounded-xl text-center">
                    <div className="text-slate-300 text-sm mb-2">Tuition</div>
                    <div className="text-emerald-400 font-mono text-xl font-bold">{college.tuition || 'Not published'}</div>
                    {college.tuition?.includes('in-state') && (
                      <div className="text-slate-300 text-xs mt-1">per year</div>
                    )}
                  </div>
                  {college.founded && (
                    <div className="p-5 bg-slate-800/50 rounded-xl text-center">
                      <div className="text-slate-300 text-sm mb-2">Founded</div>
                      <div className="text-cyan-400 font-mono text-2xl font-bold">{college.founded}</div>
                    </div>
                  )}
                  {college.match_score && (
                    <div className="p-5 bg-slate-800/50 rounded-xl text-center">
                      <div className="text-slate-300 text-sm mb-2">Match Score</div>
                      <div className="text-purple-400 font-mono text-2xl font-bold">{college.match_score}%</div>
                    </div>
                  )}
                </div>

                {/* Known For */}
                {college.known_for && (
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-3">Known For</h3>
                    <div className="flex flex-wrap gap-2">
                      {college.known_for.split(', ').map((item, i) => (
                        <span key={i} className="px-4 py-2 bg-slate-800/60 border border-slate-700 rounded-full text-slate-300">
                          {item}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Programs */}
                {college.programs && college.programs.length > 0 && (
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-3">Top Programs</h3>
                    <div className="grid sm:grid-cols-2 gap-3">
                      {college.programs.map((program, i) => (
                        <div key={i} className="p-3 bg-slate-800/40 rounded-lg border border-slate-700/50 text-slate-300">
                          {program}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Why This College */}
                {college.why_recommended && (
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-3">Why This College?</h3>
                    <p className="text-slate-300 leading-relaxed p-4 bg-gradient-to-r from-blue-600/10 to-purple-600/10 rounded-xl border border-blue-500/20">
                      {college.why_recommended}
                    </p>
                  </div>
                )}

                {/* Application Tips */}
                {college.application_tips && college.application_tips.length > 0 && (
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-3">Application Tips</h3>
                    <div className="space-y-2">
                      {college.application_tips.map((tip, i) => (
                        <div key={i} className="flex items-start gap-2 p-3 bg-slate-800/30 rounded-lg">
                          <span className="text-emerald-400">💡</span>
                          <span className="text-slate-300">{tip}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="p-6 border-t border-slate-800 flex gap-3">
                {onToggleFavorite && (
                  <Button 
                    onClick={() => {
                      onToggleFavorite(college);
                      setIsExpanded(false);
                    }}
                    className={isFavorite ? 'bg-pink-600 hover:bg-pink-700' : 'btn-primary'}
                  >
                    {isFavorite ? '💔 Remove from Favorites' : '❤️ Add to Favorites'}
                  </Button>
                )}
                <Button variant="outline" className="border-slate-700 text-slate-300 flex-1">
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Visit Website
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}